import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import BrandLogo from "@/components/BrandLogo";
import { useStoreSettings } from "@/hooks/useStoreSettings";
import { toast } from "sonner";
import {
  ShoppingCart, Search, Heart, User, Menu, ChevronDown, Shield,
  Truck, RotateCcw, Lock, MapPin, CreditCard, Smartphone,
  Building2, Package, CheckCircle, ChevronRight, Tag, Minus, Plus, X,
  Phone, Mail, Star
} from "lucide-react";
import { getCart, updateLineQty, removeFromCart, saveCart, getCartCount, toGrams, defaultVariantWeight } from "@/utils/cart";
import GstDrawer from "@/components/GstDrawer";
import { buildShipments, lineIsBackorder, useDispatchTexts, comboKey } from "@/utils/stockDispatch";
import SiteFooter from "@/components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Order Success Screen (auto-redirects to tracking in 4 s) ── */
function OrderSuccessScreen({ orderSuccess, payMethod, form, navigate }) {
  const [count, setCount] = useState(4);

  useEffect(() => {
    if (count <= 0) {
      navigate(`/orders/${orderSuccess.orderId}`);
      return;
    }
    const t = setTimeout(() => setCount(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [count, navigate, orderSuccess.orderId]);

  const payLabel = { upi: "UPI", card: "Credit/Debit Card", netbanking: "Net Banking" }[payMethod] || "Cash on Delivery";
  const pct = ((4 - count) / 4) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-orange-50 flex items-center justify-center px-4" data-testid="checkout-success">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center border border-slate-100">

        {/* Animated check */}
        <div className="relative w-20 h-20 mx-auto mb-5">
          <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
            <circle cx="40" cy="40" r="36" fill="none" stroke="#f1f5f9" strokeWidth="5" />
            <circle cx="40" cy="40" r="36" fill="none" stroke="#22c55e" strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 36}`}
              strokeDashoffset={`${2 * Math.PI * 36 * (1 - pct / 100)}`}
              style={{ transition: "stroke-dashoffset 0.9s ease" }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <CheckCircle className="w-9 h-9 text-green-500" />
          </div>
        </div>

        <h1 className="text-2xl font-black text-slate-900 mb-1">Order Placed!</h1>
        <p className="text-sm text-slate-500 mb-5">
          Your order <span className="font-bold text-slate-800">{orderSuccess.orderNumber}</span> has been confirmed.
        </p>

        {/* Summary */}
        <div className="bg-slate-50 rounded-2xl px-5 py-4 mb-5 space-y-2.5 text-left">
          {[
            ["Order Number", orderSuccess.orderNumber],
            ["Total Amount", `₹${Number(orderSuccess.total).toLocaleString("en-IN")}`],
            ["Payment", payLabel],
            ["Deliver to", `${form.city}, ${form.state}`],
          ].map(([label, value]) => (
            <div key={label} className="flex items-center justify-between text-sm">
              <span className="text-slate-400">{label}</span>
              <span className="font-semibold text-slate-800">{value}</span>
            </div>
          ))}
        </div>

        {/* Auto-redirect notice */}
        <p className="text-xs text-slate-400 mb-4">
          Redirecting to your order tracking in <span className="font-bold text-orange-500">{count}s</span>…
        </p>

        {/* Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => navigate("/")}
            className="px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:border-slate-400 transition-colors">
            Continue Shopping
          </button>
          <button onClick={() => navigate(`/orders/${orderSuccess.orderId}`)}
            data-testid="checkout-track-order-btn"
            className="px-4 py-2.5 bg-orange-500 hover:bg-orange-600 rounded-xl text-sm font-bold text-white transition-colors">
            Track Order →
          </button>
        </div>

      </div>
    </div>
  );
}

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan",
  "Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman & Nicobar Islands","Chandigarh","Dadra & Nagar Haveli","Daman & Diu",
  "Delhi","Jammu & Kashmir","Ladakh","Lakshadweep","Puducherry",
];

function fmt(n) {
  if (!n && n !== 0) return "0";
  return Number(n).toLocaleString("en-IN");
}

// ── Step Progress ─────────────────────────────────────────────────────────────
function StepProgress({ step }) {
  const steps = [
    { n: 1, label: "Shipping Address" },
    { n: 2, label: "Payment Method" },
    { n: 3, label: "Review & Place Order" },
  ];
  return (
    <div className="flex items-center justify-center mb-6 px-4" data-testid="checkout-steps">
      {steps.map((s, i) => (
        <div key={s.n} className="flex items-center">
          {i > 0 && (
            <div className={`w-20 sm:w-32 h-0.5 mx-1 ${step > i ? "bg-blue-500" : "bg-slate-200"}`} />
          )}
          <div className="flex flex-col items-center gap-1">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${
                step === s.n
                  ? "bg-blue-600 border-blue-600 text-white"
                  : step > s.n
                  ? "bg-blue-600 border-blue-600 text-white"
                  : "bg-white border-slate-300 text-slate-400"
              }`}
            >
              {step > s.n ? <CheckCircle className="w-4 h-4" /> : s.n}
            </div>
            <span className={`text-[11px] font-semibold whitespace-nowrap hidden sm:block ${step === s.n ? "text-blue-600" : step > s.n ? "text-blue-600" : "text-slate-400"}`}>
              {s.label}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Section Card ──────────────────────────────────────────────────────────────
function SectionCard({ num, title, icon: Icon, iconBg = "bg-blue-500", children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden mb-4">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
        <div className={`w-8 h-8 ${iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
          {num ? (
            <span className="text-white text-sm font-bold">{num}</span>
          ) : (
            <Icon className="w-4 h-4 text-white" />
          )}
        </div>
        <h2 className="text-base font-bold text-slate-900">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ── Input Field ───────────────────────────────────────────────────────────────
function FormInput({ label, required, value, onChange, placeholder, type = "text", maxLength, error, rightEl, testId }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-semibold text-slate-600">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          data-testid={testId}
          className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors ${
            error
              ? "border-red-400 focus:ring-red-200 bg-red-50"
              : "border-slate-300 focus:border-blue-500 focus:ring-blue-100"
          }`}
        />
        {rightEl && <div className="absolute right-2 top-1/2 -translate-y-1/2">{rightEl}</div>}
      </div>
      {error && <p className="text-xs text-red-500 flex items-center gap-1"><X className="w-3 h-3" />{error}</p>}
    </div>
  );
}

// ── Product Row in Sidebar ────────────────────────────────────────────────────
function OrderItemRow({ item, onQtyChange, onRemove }) {
  const total = item.price * item.totalQty;
  const variantLabel = item.variantLines?.[0]
    ? [item.variantLines[0].color, item.variantLines[0].size].filter(Boolean).join(", ")
    : "";

  return (
    <div className="flex gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`checkout-item-${item.id}`}>
      <div className="w-14 h-14 rounded-lg bg-slate-50 border border-slate-100 overflow-hidden flex-shrink-0">
        {item.image ? (
          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
        ) : (
          <Package className="w-6 h-6 text-slate-300 m-auto mt-4" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-slate-800 line-clamp-2 leading-snug">{item.name}</p>
        {variantLabel && <p className="text-[11px] text-slate-400 mt-0.5">{variantLabel}</p>}
        <div className="flex items-center justify-between mt-1.5">
          <div className="flex items-center border border-slate-200 rounded overflow-hidden">
            <button
              onClick={() => onQtyChange(item.id, Math.max(0, item.totalQty - 1))}
              className="w-6 h-6 flex items-center justify-center hover:bg-slate-100 transition-colors"
              data-testid={`checkout-qty-dec-${item.id}`}
            >
              <Minus className="w-3 h-3 text-slate-600" />
            </button>
            <span className="w-7 text-center text-xs font-semibold text-slate-800">{item.totalQty}</span>
            <button
              onClick={() => onQtyChange(item.id, item.totalQty + 1)}
              className="w-6 h-6 flex items-center justify-center hover:bg-slate-100 transition-colors"
              data-testid={`checkout-qty-inc-${item.id}`}
            >
              <Plus className="w-3 h-3 text-slate-600" />
            </button>
          </div>
          <span className="text-sm font-black text-slate-900">₹{fmt(total)}</span>
        </div>
      </div>
      <button
        onClick={() => onRemove(item.id)}
        className="self-start text-slate-300 hover:text-red-400 transition-colors mt-0.5"
        data-testid={`checkout-remove-${item.id}`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function CheckoutPage() {
  const navigate = useNavigate();
  const storeSettings = useStoreSettings();
  const [cart, setCart] = useState(getCart);
  const [cartCount, setCartCount] = useState(getCartCount);

  // Form state
  const [form, setForm] = useState(() => {
    const savedUser = (() => { try { return JSON.parse(localStorage.getItem("it_customer_user") || "null"); } catch { return null; } })();
    return {
      fullName: savedUser?.name || "",
      phone: "",
      email: savedUser?.email || "",
      pincode: "", address: "",
      landmark: "", city: "", state: "", saveAddress: false,
    };
  });
  const [errors, setErrors] = useState({});
  const [delivery, setDelivery] = useState("standard");
  const [payMethod, setPayMethod] = useState("upi");

  // Checkout settings (shipping slabs + delivery options + payment methods)
  const [checkoutSettings, setCheckoutSettings] = useState(null);

  // Coupon
  const [couponOpen, setCouponOpen] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [couponMsg, setCouponMsg] = useState("");
  const dispatchTexts = useDispatchTexts();

  // A coupon chosen on a product page ("Apply Coupon" button) is applied automatically here.
  useEffect(() => {
    let code = "";
    try { code = localStorage.getItem("it_pending_coupon") || ""; } catch (e) { /* storage blocked */ }
    const total = cart.reduce((s, i) => s + i.price * i.totalQty, 0);
    if (!code || couponApplied || total <= 0) return;
    (async () => {
      setCouponOpen(true); setCoupon(code); setCouponApplying(true); setCouponError(""); setCouponMsg("");
      try {
        const res = await axios.post(`${API}/coupons/validate`, { code, cart_total: total });
        setCouponDiscount(res.data.discount);
        setCouponApplied(true);
        setCouponMsg(res.data.message);
      } catch (e) {
        setCouponError(e.response?.data?.detail || "Invalid coupon code");
      } finally {
        setCouponApplying(false);
        try { localStorage.removeItem("it_pending_coupon"); } catch (e2) { /* ignore */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  // GST Invoice state (persisted in localStorage)
  const [gstList, setGstList]         = useState(() => {
    try { return JSON.parse(localStorage.getItem("it_gst_details") || "[]"); } catch { return []; }
  });
  const [selectedGstId, setSelectedGstId] = useState(null);
  const [gstOpen, setGstOpen]             = useState(false);
  const selectedGst = gstList.find(g => g.id === selectedGstId) || null;

  const handleGstSave = (entry) => {
    const isNew = !entry.id;
    const withId = isNew ? { ...entry, id: `gst_${Date.now()}` } : entry;
    const updated = isNew
      ? [...gstList, withId]
      : gstList.map(g => g.id === withId.id ? withId : g);
    setGstList(updated);
    localStorage.setItem("it_gst_details", JSON.stringify(updated));
    // Auto-select newly added entry
    if (isNew) setSelectedGstId(withId.id);
  };

  const handleGstDelete = (id) => {
    const updated = gstList.filter(g => g.id !== id);
    setGstList(updated);
    localStorage.setItem("it_gst_details", JSON.stringify(updated));
    if (selectedGstId === id) setSelectedGstId(null);
  };

  const handleGstConfirm = (entry) => {
    setSelectedGstId(entry.id);
    setGstOpen(false);
  };

  // Order state
  const [placing, setPlacing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null); // {orderId, orderNumber}
  // Mobile multi-step state (1=Address, 2=Confirm, 3=Payment)
  const [mobileStep, setMobileStep] = useState(1);
  const [cartUi, setCartUi] = useState({ delivery_days: 3, show_savings_zone: true, checkout_cta_text: "Continue" });

  // Load Razorpay script + checkout settings
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    return () => { try { document.body.removeChild(script); } catch (_) {} };
  }, []);

  useEffect(() => {
    axios.get(`${API}/checkout-settings`).then(res => {
      const s = res.data;
      setCheckoutSettings(s);
      if (s?.cart_ui) setCartUi(s.cart_ui);
      // Set delivery to first enabled option
      const firstDelivery = (s?.shipping?.delivery_options || []).find(o => o.enabled);
      if (firstDelivery) setDelivery(firstDelivery.id);
      // Set payment to first enabled: prefer "upi" if online is enabled, else first method
      const onlineEnabled = (s?.payment_methods || []).find(m => m.id === "online" && m.enabled);
      if (!onlineEnabled) {
        const firstPay = (s?.payment_methods || []).find(m => m.enabled);
        if (firstPay) setPayMethod(firstPay.id);
      }
    }).catch(() => {/* silently use defaults */});
  }, []);

  // Carts saved before variant weights were stored on each line: fill the weights in from the product
  useEffect(() => {
    const stale = getCart().filter(i => (i.variantLines || []).some(l => l.weight === undefined));
    if (!stale.length) return;
    Promise.all(stale.map(i => axios.get(`${API}/products/public/${i.slug}`).then(r => r.data).catch(() => null)))
      .then(products => {
        const bySlug = {};
        products.forEach(p => { if (p?.url_slug) bySlug[p.url_slug] = p; });
        saveCart(getCart().map(i => {
          const product = bySlug[i.slug];
          const combos = product?.variant_combinations;
          if (!combos) return i;
          return { ...i, variantLines: (i.variantLines || []).map(l => {
            if (l.weight !== undefined) return l;
            const combo = combos.find(c => comboKey(c) === l.key);
            return { ...l, weight: combo ? toGrams(combo.weight) || 0 : defaultVariantWeight(product) };
          }) };
        }));
      });
  }, []);

  // Cart reactivity
  const refreshCart = () => {
    setCart(getCart());
    setCartCount(getCartCount());
  };
  useEffect(() => {
    window.addEventListener("it_cart_updated", refreshCart);
    return () => window.removeEventListener("it_cart_updated", refreshCart);
  }, []);

  // Cart qty / remove handlers
  const handleQtyChange = (productId, newQty) => {
    if (newQty <= 0) {
      removeFromCart(productId);
    } else {
      const cart = getCart();
      const item = cart.find(i => i.id === productId);
      if (!item) return;
      const updated = cart.map(i => i.id === productId
        ? { ...i, totalQty: newQty, variantLines: i.variantLines.map(l => ({ ...l, qty: newQty })) }
        : i
      );
      saveCart(updated);
    }
    refreshCart();
  };

  const handleRemove = (productId) => {
    removeFromCart(productId);
    refreshCart();
  };

  // ── Checkout settings helpers ──────────────────────────────────────────────

  const freeThreshold = checkoutSettings?.shipping?.free_threshold ?? 999;
  const weightSlabs   = checkoutSettings?.shipping?.weight_slabs  ?? [];
  const deliveryOptions = (checkoutSettings?.shipping?.delivery_options ?? [
    { id: "standard", enabled: true, name: "Standard Delivery", description: "3–6 business days", pricing: "weight", flat_charge: 0 },
    { id: "express",  enabled: true, name: "Express Delivery",  description: "1–2 business days", pricing: "flat",   flat_charge: 99 },
  ]).filter(o => o.enabled);
  const paymentMethods = (checkoutSettings?.payment_methods ?? [
    { id: "online", enabled: true,  name: "Online Payment",   description: "Pay via UPI / Card / Net Banking", surcharge_type: "none",  surcharge_value: 0 },
    { id: "cod",    enabled: true,  name: "Cash on Delivery", description: "Pay at the time of delivery",      surcharge_type: "flat",  surcharge_value: 25 },
  ]).filter(m => m.enabled);

  // In-stock lines ship together; out-of-stock (backorder) lines ship separately
  const shipments = buildShipments(cart);
  // Shown order weight = the same per-line (variant) weights the shipping charge is computed from
  const totalWeightGrams = shipments.reduce((s, sh) => s + sh.weightG, 0);

  const extraAboveLastSlab = checkoutSettings?.shipping?.extra_above_last_slab || {};

  // Shipping charge for a shipment weight. A slab covers its range up to and including its max
  // (e.g. "500g – 1kg" includes 1000 g). Above the heaviest slab, the configured charge is added
  // for every started step (e.g. +₹70 per 500 g); without it, the last slab's charge applies.
  function slabCharge(weightG) {
    if (!weightSlabs.length) return 0;
    const slab = weightSlabs.find(s => weightG >= s.min_g && (s.max_g === null || s.max_g === undefined || weightG <= s.max_g));
    if (slab) return slab.charge || 0;
    const top = weightSlabs.reduce((a, s) => (Number(s.max_g) > Number(a.max_g) ? s : a), weightSlabs[0]);
    const stepG = Number(extraAboveLastSlab.step_g) || 500;
    const stepCharge = Number(extraAboveLastSlab.charge ?? 70) || 0;   // store default: +₹70 per 500 g; 0 turns it off
    if (stepCharge > 0 && top.max_g != null && weightG > top.max_g) {
      return (top.charge || 0) + Math.ceil((weightG - top.max_g) / stepG) * stepCharge;
    }
    return weightSlabs[weightSlabs.length - 1].charge || 0; // fallback to last
  }

  // Delivery charge for a given delivery option
  function getDeliveryCharge(option) {
    if (!option) return 0;
    if (option.pricing === "free") return 0;
    if (option.pricing === "flat") return option.flat_charge || 0;
    // weight-based: one charge per shipment, each from its own weight
    return shipments.reduce((sum, sh) => sum + slabCharge(sh.weightG), 0);
  }

  // Payment surcharge for a given payMethod
  function getPaymentSurcharge(pm, sub) {
    const isOnline = ["upi", "card", "netbanking"].includes(pm);
    const methodId = isOnline ? "online" : pm;
    const method = paymentMethods.find(m => m.id === methodId);
    if (!method) return 0;
    if (method.surcharge_type === "flat")    return method.surcharge_value || 0;
    if (method.surcharge_type === "percent") return Math.round(sub * (method.surcharge_value || 0) / 100);
    return 0;
  }

  // Price calculations
  const subtotal     = cart.reduce((s, i) => s + i.price * i.totalQty, 0);
  const mrpTotal     = cart.reduce((s, i) => s + (i.mrp || i.price) * i.totalQty, 0);
  const itemDiscount = Math.max(0, mrpTotal - subtotal);
  const selectedDeliveryOption = deliveryOptions.find(o => o.id === delivery) || deliveryOptions[0];
  const rawDelivery  = getDeliveryCharge(selectedDeliveryOption);
  const shippingCharge = freeThreshold > 0 && subtotal >= freeThreshold ? 0 : rawDelivery;
  const freeShipApplied = freeThreshold > 0 && subtotal >= freeThreshold;
  const displayShipments = shipments.map((sh, i) => ({
    ...sh,
    name: `Shipment ${i + 1}`,
    dispatchText: sh.id === "backorder" ? dispatchTexts.outOfStock : dispatchTexts.inStock,
    charge: freeShipApplied || selectedDeliveryOption?.pricing !== "weight" ? 0 : slabCharge(sh.weightG),
  }));
  const paymentSurcharge = getPaymentSurcharge(payMethod, subtotal);
  const total        = Math.max(0, subtotal + shippingCharge + paymentSurcharge - couponDiscount);
  const totalItems   = cart.reduce((s, i) => s + i.totalQty, 0);

  // Form set helper
  const set = (field) => (val) => setForm(f => ({ ...f, [field]: val }));

  // Validation
  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Name is required";
    if (!form.phone.trim()) e.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(form.phone.replace(/\s/g, ""))) e.phone = "Enter a valid 10-digit mobile number";
    if (!form.email.trim()) e.email = "Email address is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address";
    if (!form.pincode.trim()) e.pincode = "Pincode is required";
    else if (!/^\d{6}$/.test(form.pincode)) e.pincode = "Enter a valid 6-digit pincode";
    if (!form.address.trim()) e.address = "Address is required";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.state) e.state = "Please select a state";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) { toast.error("Your cart is empty"); return; }
    if (!validate()) {
      if (mobileStep === 3) setMobileStep(1);
      toast.error("Please fill in all required fields");
      return;
    }

    const deliveryAddress = [form.address, form.landmark, form.city, form.state, form.pincode]
      .filter(Boolean).join(", ");

    // One order row per variant line (so every variant is recorded); a single-line item stays one row
    const items = cart.flatMap(item => {
      const lines = item.variantLines || [];
      const perLine = lines.length > 1 && lines.reduce((s, l) => s + Number(l.qty || 0), 0) === item.totalQty;
      const base = { product_id: item.id, name: item.name, image: item.image || "", price: item.price };
      if (!perLine) {
        return [{
          ...base,
          quantity: item.totalQty,
          amount: item.price * item.totalQty,
          variant: lines[0] ? [lines[0].color, lines[0].size].filter(Boolean).join(", ") : "",
          ...(item.stockMode && lines[0] ? { shipment: lineIsBackorder(item, lines[0]) ? "backorder" : "in_stock" } : {}),
        }];
      }
      return lines.map(l => ({
        ...base,
        quantity: l.qty,
        amount: item.price * l.qty,
        variant: [l.color, l.size].filter(Boolean).join(", "),
        ...(item.stockMode ? { shipment: lineIsBackorder(item, l) ? "backorder" : "in_stock" } : {}),
      }));
    });
    const shipmentsPayload = displayShipments.length > 1 ? displayShipments.map(sh => ({
      name: sh.name,
      dispatch_text: sh.dispatchText,
      weight_g: sh.weightG,
      delivery_charge: sh.charge,
      items: sh.rows.map(r => ({
        product_id: r.item.id,
        name: r.item.name,
        variant: [r.line.color, r.line.size].filter(Boolean).join(", "),
        quantity: r.line.qty,
      })),
    })) : [];

    // COD — place order directly
    if (payMethod === "cod" || !["upi", "card", "netbanking"].includes(payMethod)) {
      setPlacing(true);
      try {
        const res = await axios.post(`${API}/orders`, {
          customer_name: form.fullName,
          customer_email: form.email,
          customer_phone: form.phone,
          delivery_address: deliveryAddress,
          items,
          shipments: shipmentsPayload,
          payment_method: payMethod === "cod" ? "COD" : payMethod,
          delivery_charge: shippingCharge,
          payment_surcharge: paymentSurcharge,
          coupon_code: couponApplied ? coupon : "",
          coupon_discount: couponDiscount,
          gst_details: selectedGst ? { gstin: selectedGst.gstin, business_name: selectedGst.business_name } : null,
          notes: "",
        });
        setOrderSuccess({ orderId: res.data.id, orderNumber: res.data.order_number, total });
        saveCart([]);
        refreshCart();
        // Mark cart as converted to prevent abandoned-cart emails
        const ctoken = localStorage.getItem("it_customer_token");
        if (ctoken) axios.post(`${API}/customer/cart/mark-converted`, {}, { headers: { Authorization: `Bearer ${ctoken}` } }).catch(() => {});
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to place order. Please try again.");
      } finally {
        setPlacing(false);
      }
      return;
    }

    // Online payment — create Razorpay order first
    setPlacing(true);
    try {
      const amountPaise = total * 100;
      const rzpRes = await axios.post(`${API}/payments/razorpay/create-order`, {
        amount: amountPaise,
        currency: "INR",
        receipt: `rcpt${Date.now()}`.slice(0, 40),
      });

      const { razorpay_order_id, key_id } = rzpRes.data;

      const options = {
        key: key_id,
        amount: amountPaise,
        currency: "INR",
        order_id: razorpay_order_id,
        name: storeSettings?.store_name || "Store",
        description: `Order for ${totalItems} item${totalItems !== 1 ? "s" : ""}`,
        prefill: {
          name: form.fullName,
          contact: form.phone,
          email: form.email,
        },
        theme: { color: "#f97316" },
        handler: async (response) => {
          // Payment success — verify on backend and create order
          try {
            const verifyRes = await axios.post(`${API}/payments/razorpay/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              customer_name: form.fullName,
              customer_email: form.email,
              customer_phone: form.phone,
              delivery_address: deliveryAddress,
              items,
              shipments: shipmentsPayload,
              payment_method: "Razorpay",
              delivery_charge: shippingCharge,
              payment_surcharge: paymentSurcharge,
              coupon_code: couponApplied ? coupon : "",
              coupon_discount: couponDiscount,
              gst_details: selectedGst ? { gstin: selectedGst.gstin, business_name: selectedGst.business_name } : null,
              notes: "",
            });
            setOrderSuccess({ orderId: verifyRes.data.id, orderNumber: verifyRes.data.order_number, total });
            saveCart([]);
            refreshCart();
            const ctoken2 = localStorage.getItem("it_customer_token");
            if (ctoken2) axios.post(`${API}/customer/cart/mark-converted`, {}, { headers: { Authorization: `Bearer ${ctoken2}` } }).catch(() => {});
          } catch (err) {
            toast.error("Payment received but order creation failed. Please contact support.");
          } finally {
            setPlacing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPlacing(false);
            toast.info("Payment cancelled. You can try again.");
          },
        },
      };

      if (!window.Razorpay) {
        toast.error("Payment gateway not loaded. Please refresh and try again.");
        setPlacing(false);
        return;
      }
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp.error?.description || "Unknown error"}`);
        setPlacing(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to initiate payment. Please try again.");
      setPlacing(false);
    }
  };

  // ── Delivery date ─────────────────────────────────────────────────────────
  const deliveryDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + (cartUi.delivery_days || 3));
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  })();

  // ── Mobile step validation (step 1 → 2 advance) ───────────────────────────
  const validateMobileStep1 = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Name is required";
    if (!form.phone.trim()) e.phone = "Phone required";
    else if (!/^\d{10}$/.test(form.phone.replace(/\s/g, ""))) e.phone = "10-digit mobile";
    if (!form.email.trim()) e.email = "Email required";
    if (!form.pincode.trim()) e.pincode = "Pincode required";
    else if (!/^\d{6}$/.test(form.pincode)) e.pincode = "6-digit pincode";
    if (!form.address.trim()) e.address = "Address required";
    if (!form.city.trim()) e.city = "City required";
    if (!form.state) e.state = "Select state";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Mobile step CTA labels ─────────────────────────────────────────────────
  const mobileCta = mobileStep === 3 ? (placing ? "Placing Order…" : "Place Order") : (cartUi.checkout_cta_text || "Continue");

  const handleMobileCta = () => {
    if (mobileStep === 1) {
      if (validateMobileStep1()) setMobileStep(2);
      else toast.error("Please fill in all required fields");
    } else if (mobileStep === 2) {
      setMobileStep(3);
    } else {
      handlePlaceOrder();
    }
  };

  // ── Order Success Screen ────────────────────────────────────────────────────
  if (orderSuccess) {
    return <OrderSuccessScreen orderSuccess={orderSuccess} payMethod={payMethod} form={form} navigate={navigate} />;
  }

  // ── Empty Cart ─────────────────────────────────────────────────────────────
  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50">
        <CheckoutHeader cartCount={0} />
        <div className="max-w-xl mx-auto px-4 py-20 text-center">
          <ShoppingCart className="w-16 h-16 text-slate-200 mx-auto mb-4" />
          <h2 className="text-xl font-black text-slate-800 mb-2">Your cart is empty</h2>
          <p className="text-slate-500 mb-6 text-sm">Add items to your cart before checking out.</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl transition-colors text-sm">
            <Package className="w-4 h-4" /> Shop Now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
    {/* ═══════════════════════════ MOBILE CHECKOUT ════════════════════════════ */}
    <div className="block md:hidden bg-[#f5f7fa] min-h-screen">
      {/* Mobile Step Header */}
      <header className="bg-white sticky top-0 z-40 shadow-sm">
        <div className="flex items-center px-4 py-3 gap-3">
          <button
            onClick={() => mobileStep > 1 ? setMobileStep(s => s - 1) : navigate("/cart")}
            className="p-1 -ml-1 rounded-full hover:bg-slate-100 transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-slate-700 rotate-180" />
          </button>
          <div className="flex-1">
            <p className="text-base font-bold text-slate-900">
              {mobileStep === 1 ? "Shipping Address" : mobileStep === 2 ? "Confirm Details" : "Payments"}
            </p>
          </div>
          {mobileStep === 3 && (
            <div className="flex items-center gap-1 text-[11px] font-semibold text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
              <Shield className="w-3 h-3" /> 100% Secure
            </div>
          )}
        </div>
        {/* Step indicator */}
        <div className="flex items-center px-4 pb-3 gap-1.5">
          {[1, 2, 3].map((s, i) => (
            <div key={s} className="flex items-center gap-1.5 flex-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 flex-shrink-0 transition-all ${
                mobileStep > s ? "bg-green-500 border-green-500 text-white" :
                mobileStep === s ? "bg-orange-500 border-orange-500 text-white" :
                "bg-white border-slate-300 text-slate-400"
              }`}>
                {mobileStep > s ? "✓" : s}
              </div>
              <span className={`text-[10px] font-semibold truncate ${
                mobileStep === s ? "text-orange-600" : mobileStep > s ? "text-green-600" : "text-slate-400"
              }`}>
                {["Address", "Confirm", "Payment"][i]}
              </span>
              {i < 2 && <div className={`flex-1 h-px ${mobileStep > s ? "bg-green-400" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>
      </header>

      {/* ── Step 1: Address ── */}
      {mobileStep === 1 && (
        <div className="px-4 py-4 pb-36 space-y-4">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Contact Details</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Full Name *</label>
                <input value={form.fullName} onChange={e => set("fullName")(e.target.value)} placeholder="Enter your full name" className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.fullName ? "border-red-400 focus:ring-red-100 bg-red-50" : "border-slate-300 focus:border-orange-400 focus:ring-orange-100"}`} data-testid="checkout-name-input" />
                {errors.fullName && <p className="text-xs text-red-500 mt-1">{errors.fullName}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Phone Number *</label>
                <input value={form.phone} onChange={e => set("phone")(e.target.value)} placeholder="10-digit mobile number" type="tel" maxLength={10} className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.phone ? "border-red-400 focus:ring-red-100 bg-red-50" : "border-slate-300 focus:border-orange-400 focus:ring-orange-100"}`} data-testid="checkout-phone-input" />
                {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Email Address *</label>
                <input value={form.email} onChange={e => set("email")(e.target.value)} placeholder="For order confirmation" type="email" className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.email ? "border-red-400 focus:ring-red-100 bg-red-50" : "border-slate-300 focus:border-orange-400 focus:ring-orange-100"}`} data-testid="checkout-email-input" />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Delivery Address</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Pincode *</label>
                <input value={form.pincode} onChange={e => set("pincode")(e.target.value)} placeholder="6-digit pincode" maxLength={6} className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.pincode ? "border-red-400 focus:ring-red-100 bg-red-50" : "border-slate-300 focus:border-orange-400 focus:ring-orange-100"}`} />
                {errors.pincode && <p className="text-xs text-red-500 mt-1">{errors.pincode}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Address *</label>
                <input value={form.address} onChange={e => set("address")(e.target.value)} placeholder="House No, Building, Street" className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.address ? "border-red-400 focus:ring-red-100 bg-red-50" : "border-slate-300 focus:border-orange-400 focus:ring-orange-100"}`} />
                {errors.address && <p className="text-xs text-red-500 mt-1">{errors.address}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Landmark (Optional)</label>
                <input value={form.landmark} onChange={e => set("landmark")(e.target.value)} placeholder="Near metro, hospital, etc." className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">City *</label>
                  <input value={form.city} onChange={e => set("city")(e.target.value)} placeholder="City" className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.city ? "border-red-400 focus:ring-red-100 bg-red-50" : "border-slate-300 focus:border-orange-400 focus:ring-orange-100"}`} />
                  {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">State *</label>
                  <select value={form.state} onChange={e => set("state")(e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 bg-white ${errors.state ? "border-red-400 focus:ring-red-100" : "border-slate-300 focus:border-orange-400 focus:ring-orange-100"}`} data-testid="checkout-state-select">
                    <option value="">State</option>
                    {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.state && <p className="text-xs text-red-500 mt-1">{errors.state}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Delivery options */}
          {deliveryOptions.length > 0 && (
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Delivery Option</p>
              <div className="space-y-2">
                {deliveryOptions.map(opt => {
                  const rawCharge = getDeliveryCharge(opt);
                  const isFree = (freeThreshold > 0 && subtotal >= freeThreshold) || opt.pricing === "free";
                  return (
                    <label key={opt.id} className={`flex items-start gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${delivery === opt.id ? "border-orange-400 bg-orange-50" : "border-slate-200"}`} data-testid={`checkout-delivery-${opt.id}`}>
                      <input type="radio" name="mob-delivery" value={opt.id} checked={delivery === opt.id} onChange={() => setDelivery(opt.id)} className="mt-0.5 accent-orange-500" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-bold text-slate-800">{opt.name}</span>
                          {isFree ? <span className="text-[10px] font-bold bg-green-500 text-white px-1.5 py-0.5 rounded">FREE</span>
                            : <span className="text-sm font-black text-slate-900">₹{fmt(isFree ? 0 : rawCharge)}</span>}
                        </div>
                        {opt.description && <p className="text-xs text-slate-500 mt-0.5">{opt.description}</p>}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Step 2: Confirm Details ── */}
      {mobileStep === 2 && (
        <div className="pb-36 space-y-2">
          {/* Delivering to card */}
          <div className="bg-white px-4 py-4 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Delivering to</p>
                <p className="text-sm font-bold text-slate-900">{form.fullName}</p>
                <p className="text-xs text-slate-600 mt-0.5">{form.address}{form.landmark ? `, ${form.landmark}` : ""}, {form.city}, {form.state} – {form.pincode}</p>
                <p className="text-xs text-slate-500 mt-0.5">{form.phone}</p>
              </div>
              <button onClick={() => setMobileStep(1)} className="text-xs font-bold text-orange-500 border border-orange-400 px-3 py-1 rounded-full hover:bg-orange-50 flex-shrink-0 transition-colors">Change</button>
            </div>
          </div>

          {/* Order items */}
          {cart.map(item => {
            const discPct = item.mrp && item.mrp > item.price ? Math.round(((item.mrp - item.price) / item.mrp) * 100) : 0;
            return (
              <div key={item.id} className="bg-white px-4 py-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-20 h-24 bg-slate-50 border border-slate-100 rounded overflow-hidden flex items-center justify-center">
                    {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-contain p-2" loading="lazy" decoding="async" /> : <Package className="w-6 h-6 text-slate-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 line-clamp-2 leading-snug">{item.name}</p>
                    {item.variantLines?.[0] && <p className="text-[11px] text-slate-500 mt-0.5">{[item.variantLines[0].color, item.variantLines[0].size].filter(Boolean).join(", ")}</p>}
                    <div className="flex items-baseline gap-1.5 mt-1.5">
                      {discPct > 0 && <span className="text-xs font-bold text-green-600">↓{discPct}%</span>}
                      {item.mrp > item.price && <span className="text-xs text-slate-400 line-through">₹{item.mrp.toLocaleString()}</span>}
                      <span className="text-base font-bold text-slate-900">₹{item.price.toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1"><Truck className="w-3 h-3" />Delivery by <strong className="text-slate-700 ml-0.5">{deliveryDate}</strong></p>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Savings zone */}
          {cartUi.show_savings_zone && (
            <div className="bg-white border-t border-slate-200">
              <button onClick={() => setCouponOpen(v => !v)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors" data-testid="checkout-coupon-toggle">
                <div className="flex items-center gap-2">
                  <span className="bg-slate-800 text-white text-[10px] font-black px-2 py-0.5 rounded tracking-wider">WOW!</span>
                  <span className="text-sm font-bold text-slate-800">Savings zone</span>
                  {couponApplied && <span className="text-xs font-semibold text-green-600">— saving ₹{fmt(couponDiscount)}</span>}
                </div>
                <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${couponOpen ? "rotate-90" : ""}`} />
              </button>
              {couponOpen && (
                <div className="px-4 pb-4">
                  <div className="flex gap-2">
                    <input type="text" value={coupon} onChange={e => { setCoupon(e.target.value.toUpperCase()); setCouponError(""); }} placeholder="Enter coupon code" disabled={couponApplied} data-testid="checkout-coupon-input" className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:border-orange-400 disabled:bg-slate-50 disabled:text-slate-400" />
                    {couponApplied
                      ? <button onClick={() => { setCouponApplied(false); setCouponDiscount(0); setCoupon(""); }} className="px-4 bg-red-50 text-red-500 text-sm font-bold rounded-lg border border-red-200">Remove</button>
                      : <button onClick={async () => { if (!coupon.trim()) return; setCouponApplying(true); try { const r = await axios.post(`${API}/coupons/validate`, { code: coupon, cart_total: subtotal }); setCouponDiscount(r.data.discount); setCouponApplied(true); } catch (e) { setCouponError(e.response?.data?.detail || "Invalid code"); } finally { setCouponApplying(false); } }} disabled={couponApplying} data-testid="checkout-coupon-apply" className="px-4 bg-orange-500 text-white text-sm font-bold rounded-lg disabled:opacity-50">{couponApplying ? "…" : "Apply"}</button>}
                  </div>
                  {couponError && <p className="text-xs text-red-500 mt-1">{couponError}</p>}
                </div>
              )}
            </div>
          )}

          {/* Price details */}
          <div className="bg-white px-4 py-4">
            <p className="text-sm font-bold text-slate-700 mb-3">Price Details</p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm"><span className="text-slate-600">Price ({totalItems} items)</span><span className="font-medium text-slate-800">₹{fmt(mrpTotal)}</span></div>
              {itemDiscount > 0 && <div className="flex justify-between text-sm"><span className="text-slate-600">Discount</span><span className="font-semibold text-green-600">−₹{fmt(itemDiscount)}</span></div>}
              <div className="flex justify-between text-sm"><span className="text-slate-600">Delivery</span><span className={`font-semibold ${shippingCharge === 0 ? "text-green-600" : "text-slate-800"}`}>{shippingCharge === 0 ? "FREE" : `₹${fmt(shippingCharge)}`}</span></div>
              {couponDiscount > 0 && <div className="flex justify-between text-sm"><span className="text-slate-600">Coupon</span><span className="font-semibold text-green-600">−₹{fmt(couponDiscount)}</span></div>}
            </div>
            <div className="border-t border-dashed border-slate-200 mt-3 pt-3">
              <div className="flex justify-between"><span className="text-sm font-black text-slate-900">Total Amount</span><span className="text-sm font-black text-slate-900">₹{fmt(total)}</span></div>
            </div>
            {(itemDiscount + couponDiscount) > 0 && (
              <div className="mt-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs font-semibold text-green-700 text-center">
                You'll save ₹{fmt(itemDiscount + couponDiscount)} on this order
              </div>
            )}
            <div className="flex items-center gap-1.5 mt-3 text-[11px] text-slate-400">
              <Shield className="w-3.5 h-3.5 flex-shrink-0" />
              <span>Safe & Secure Payments · Easy Returns · 100% Authentic</span>
            </div>
            {/* GST Invoice */}
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button data-testid="gst-checkbox" onClick={() => setGstOpen(true)} className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${selectedGst ? "bg-orange-500 border-orange-500" : "border-slate-300"}`}>
                  {selectedGst && <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </button>
                <span className="text-sm font-semibold text-slate-800">GST Invoice</span>
              </div>
              <button data-testid="gst-change-btn" onClick={() => setGstOpen(true)} className="text-xs font-bold text-orange-500">{selectedGst ? "Change" : "Add"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 3: Payment ── */}
      {mobileStep === 3 && (
        <div className="pb-36 space-y-2">
          {/* Total amount row */}
          <div className="bg-white px-4 py-4 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-bold text-slate-900">Total Amount</span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </div>
            <span className="text-base font-black text-slate-900">₹{fmt(total)}</span>
          </div>

          {/* Payment methods */}
          <div className="bg-white shadow-sm">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Select Payment Method</p>
            </div>
            <div className="divide-y divide-slate-100">
              {paymentMethods.find(m => m.id === "online") && (
                <>
                  {[
                    { id: "upi", label: "UPI", sub: "Google Pay, PhonePe, Paytm & more", icon: Smartphone },
                    { id: "card", label: "Credit / Debit / ATM Card", sub: "Visa, Mastercard, RuPay, Amex", icon: CreditCard },
                    { id: "netbanking", label: "Net Banking", sub: "All major banks supported", icon: Building2 },
                  ].map(({ id, label, sub, icon: Icon }) => (
                    <label key={id} data-testid={`checkout-pay-${id}`} className={`flex items-center gap-3 px-4 py-3.5 cursor-pointer transition-colors ${payMethod === id ? "bg-orange-50" : "hover:bg-slate-50"}`}>
                      <input type="radio" name="mob-payment" value={id} checked={payMethod === id} onChange={() => setPayMethod(id)} className="accent-orange-500 flex-shrink-0" />
                      <Icon className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800">{label}</p>
                        <p className="text-xs text-slate-500">{sub}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                    </label>
                  ))}
                </>
              )}
              {paymentMethods.filter(m => m.id !== "online").map(m => {
                const surcharge = getPaymentSurcharge(m.id, subtotal);
                return (
                  <label key={m.id} data-testid={`checkout-pay-${m.id}`} className={`flex items-center gap-3 px-4 py-3.5 cursor-pointer transition-colors ${payMethod === m.id ? "bg-orange-50" : "hover:bg-slate-50"}`}>
                    <input type="radio" name="mob-payment" value={m.id} checked={payMethod === m.id} onChange={() => setPayMethod(m.id)} className="accent-orange-500 flex-shrink-0" />
                    <Package className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{m.name}</p>
                      {m.description && <p className="text-xs text-slate-500">{m.description}</p>}
                    </div>
                    {surcharge > 0 && <span className="text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded flex-shrink-0">+₹{fmt(surcharge)}</span>}
                    <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                  </label>
                );
              })}
            </div>
          </div>

          {/* Security note */}
          <div className="px-4 py-3 flex items-center gap-1.5 text-[11px] text-slate-400">
            <Lock className="w-3.5 h-3.5 flex-shrink-0" />
            <span>100% secure payment. Your data is encrypted and safe.</span>
          </div>
        </div>
      )}

      {/* ── Mobile Sticky Bottom Bar ── */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-[0_-4px_24px_rgba(0,0,0,0.12)]" data-testid="mobile-checkout-bottom-bar">
        {(itemDiscount + couponDiscount) > 0 && (
          <div className="bg-[#e8f5e9] px-4 py-1.5 flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5 text-[#2e7d32] flex-shrink-0" />
            <span className="text-xs font-semibold text-[#2e7d32]">You'll save ₹{fmt(itemDiscount + couponDiscount)} on this order</span>
          </div>
        )}
        <div className="flex items-center justify-between px-4 py-3 gap-3">
          <div className="min-w-0">
            {mrpTotal > subtotal && <p className="text-xs text-slate-400 line-through leading-none">₹{fmt(mrpTotal)}</p>}
            <p className="text-xl font-black text-slate-900 leading-tight">₹{fmt(total)}</p>
          </div>
          <button
            onClick={handleMobileCta}
            disabled={mobileStep === 3 && placing}
            data-testid="mobile-checkout-cta-btn"
            className="bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:opacity-60 text-white font-bold px-8 py-3 rounded-full text-sm flex-shrink-0 shadow-md transition-colors"
          >
            {mobileCta} {mobileStep < 3 && "→"}
          </button>
        </div>
      </div>
    </div>

    {/* ═══════════════════════════ DESKTOP CHECKOUT ═══════════════════════════ */}
    <div className="hidden md:block min-h-screen bg-[#f5f7fa]">
      <CheckoutHeader cartCount={cartCount} freeThreshold={freeThreshold} />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Page Title */}
        <div className="flex items-center gap-3 mb-6" data-testid="checkout-page-header">
          <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
            <Lock className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-black text-slate-900">Secure Checkout</h1>
            <p className="text-xs text-slate-500">Complete your order in a few simple steps</p>
          </div>
        </div>

        {/* Step Progress */}
        <StepProgress step={1} />

        <div className="flex flex-col lg:flex-row gap-5 items-start">
          {/* ─── LEFT: Forms ─────────────────────────────────────────── */}
          <div className="flex-1 min-w-0">

            {/* Shipping Address */}
            <SectionCard num="1" title="Shipping Address">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-1">
                    <FormInput
                      label="Full Name" required
                      value={form.fullName} onChange={set("fullName")}
                      placeholder="Enter your full name"
                      error={errors.fullName}
                    />
                  </div>
                  <div>
                    <FormInput
                      label="Phone Number" required type="tel"
                      value={form.phone} onChange={set("phone")}
                      placeholder="Enter mobile number"
                      maxLength={10} error={errors.phone}
                    />
                  </div>
                  <div>
                    <FormInput
                      label="Pincode" required
                      value={form.pincode} onChange={set("pincode")}
                      placeholder="Enter pincode"
                      maxLength={6} error={errors.pincode}
                      rightEl={
                        form.pincode.length === 6 ? (
                          <button
                            className="text-xs text-blue-600 font-bold hover:text-blue-700"
                            onClick={() => toast.success("Pincode verified!")}
                            data-testid="checkout-check-pincode"
                          >
                            Check
                          </button>
                        ) : null
                      }
                    />
                  </div>
                </div>

                <FormInput
                  label="Email Address" required type="email"
                  value={form.email} onChange={set("email")}
                  placeholder="Your email for order confirmation"
                  error={errors.email}
                  testId="checkout-email-input"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormInput
                    label="Address (House No, Building, Street)" required
                    value={form.address} onChange={set("address")}
                    placeholder="Enter your address"
                    error={errors.address}
                  />
                  <FormInput
                    label="Landmark (Optional)"
                    value={form.landmark} onChange={set("landmark")}
                    placeholder="E.g. near metro station"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormInput
                    label="City" required
                    value={form.city} onChange={set("city")}
                    placeholder="Enter city"
                    error={errors.city}
                  />
                  <div className="space-y-1">
                    <label className="block text-xs font-semibold text-slate-600">
                      State <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.state}
                      onChange={e => set("state")(e.target.value)}
                      data-testid="checkout-state-select"
                      className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors bg-white ${
                        errors.state
                          ? "border-red-400 focus:ring-red-200"
                          : "border-slate-300 focus:border-blue-500 focus:ring-blue-100"
                      }`}
                    >
                      <option value="">Select state</option>
                      {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {errors.state && <p className="text-xs text-red-500">{errors.state}</p>}
                  </div>
                </div>

                <label className="flex items-center gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={form.saveAddress}
                    onChange={e => set("saveAddress")(e.target.checked)}
                    className="w-4 h-4 accent-blue-600 rounded"
                  />
                  <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                    Save this address for future orders
                  </span>
                </label>
              </div>
            </SectionCard>

            {/* Delivery Options */}
            <SectionCard num={null} icon={Truck} iconBg="bg-blue-500" title="Delivery Options">
              {deliveryOptions.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No delivery options configured.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {deliveryOptions.map(opt => {
                    const rawCharge = getDeliveryCharge(opt);
                    const isFree = (freeThreshold > 0 && subtotal >= freeThreshold) || opt.pricing === "free";
                    const displayCharge = isFree ? 0 : rawCharge;
                    return (
                      <label
                        key={opt.id}
                        data-testid={`checkout-delivery-${opt.id}`}
                        className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          delivery === opt.id ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300 bg-white"
                        }`}
                      >
                        <input
                          type="radio" name="delivery" value={opt.id}
                          checked={delivery === opt.id}
                          onChange={() => setDelivery(opt.id)}
                          className="mt-0.5 accent-blue-600"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-bold text-slate-800">{opt.name}</span>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {isFree ? (
                                <>
                                  {rawCharge > 0 && <span className="text-xs line-through text-slate-400">₹{fmt(rawCharge)}</span>}
                                  <span className="text-[10px] font-bold bg-green-500 text-white px-1.5 py-0.5 rounded">FREE</span>
                                </>
                              ) : (
                                <span className="text-sm font-black text-slate-900">₹{fmt(displayCharge)}</span>
                              )}
                            </div>
                          </div>
                          {opt.description && <p className="text-xs text-slate-500 mt-0.5">{opt.description}</p>}
                          {opt.pricing === "weight" && totalWeightGrams > 0 && (
                            <p className="text-[10px] text-slate-400 mt-0.5">
                              Order weight: {totalWeightGrams >= 1000 ? `${(totalWeightGrams/1000).toFixed(2)}kg` : `${totalWeightGrams}g`}
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {freeThreshold > 0 && subtotal < freeThreshold && deliveryOptions.some(o => o.pricing !== "free") && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex items-center gap-2">
                  <Truck className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <p className="text-xs text-amber-700">
                    Add ₹{fmt(freeThreshold - subtotal)} more for <strong>FREE delivery</strong>
                  </p>
                </div>
              )}
            </SectionCard>

            {/* Payment Method */}
            <SectionCard num={null} icon={CreditCard} iconBg="bg-blue-500" title="Payment Method">
              <div className="space-y-3">
                {/* Online Payment (UPI / Card / Net Banking via Razorpay) */}
                {paymentMethods.find(m => m.id === "online") && (() => {
                  const onlineMethod = paymentMethods.find(m => m.id === "online");
                  const onlineSurcharge = getPaymentSurcharge("upi", subtotal);
                  return (
                    <>
                      {/* UPI */}
                      <label
                        data-testid="checkout-pay-upi"
                        className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${payMethod === "upi" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                      >
                        <input type="radio" name="payment" value="upi" checked={payMethod === "upi"} onChange={() => setPayMethod("upi")} className="accent-blue-600 flex-shrink-0" />
                        <Smartphone className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        <span className="flex-1 text-sm font-semibold text-slate-800">UPI (Google Pay, PhonePe, Paytm)</span>
                        {onlineSurcharge > 0 && <span className="text-xs text-slate-500 flex-shrink-0">+₹{fmt(onlineSurcharge)}</span>}
                        <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
                          {["G Pay","PhonePe","Paytm","UPI"].map(b => <span key={b} className="text-[10px] font-bold bg-white border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded">{b}</span>)}
                        </div>
                      </label>
                      {/* Card */}
                      <label
                        data-testid="checkout-pay-card"
                        className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${payMethod === "card" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                      >
                        <input type="radio" name="payment" value="card" checked={payMethod === "card"} onChange={() => setPayMethod("card")} className="accent-blue-600 flex-shrink-0" />
                        <CreditCard className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        <span className="flex-1 text-sm font-semibold text-slate-800">Credit / Debit Card</span>
                        {onlineSurcharge > 0 && <span className="text-xs text-slate-500 flex-shrink-0">+₹{fmt(onlineSurcharge)}</span>}
                        <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
                          {["VISA","MC","RuPay","Amex"].map(b => <span key={b} className="text-[10px] font-bold bg-white border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded">{b}</span>)}
                        </div>
                      </label>
                      {/* Net Banking */}
                      <label
                        data-testid="checkout-pay-netbanking"
                        className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${payMethod === "netbanking" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                      >
                        <input type="radio" name="payment" value="netbanking" checked={payMethod === "netbanking"} onChange={() => setPayMethod("netbanking")} className="accent-blue-600 flex-shrink-0" />
                        <Building2 className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        <span className="flex-1 text-sm font-semibold text-slate-800">Net Banking</span>
                        {onlineSurcharge > 0 && <span className="text-xs text-slate-500 flex-shrink-0">+₹{fmt(onlineSurcharge)}</span>}
                      </label>
                    </>
                  );
                })()}

                {/* COD + Custom methods from settings */}
                {paymentMethods.filter(m => m.id !== "online").map(m => {
                  const surcharge = getPaymentSurcharge(m.id, subtotal);
                  return (
                    <label
                      key={m.id}
                      data-testid={`checkout-pay-${m.id}`}
                      className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${payMethod === m.id ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                    >
                      <input type="radio" name="payment" value={m.id} checked={payMethod === m.id} onChange={() => setPayMethod(m.id)} className="accent-blue-600 flex-shrink-0" />
                      <Package className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold text-slate-800">{m.name}</span>
                        {m.description && <p className="text-xs text-slate-500 mt-0.5">{m.description}</p>}
                      </div>
                      {surcharge > 0 && (
                        <span className="text-xs font-semibold text-amber-600 flex-shrink-0 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                          +₹{fmt(surcharge)} fee
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </SectionCard>

            {/* Mobile Place Order */}
            <div className="lg:hidden mt-2">
              <button
                onClick={handlePlaceOrder}
                disabled={placing}
                data-testid="checkout-place-order-mobile"
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-4 rounded-xl transition-colors text-base shadow-lg"
              >
                <Lock className="w-4 h-4" />
                {placing ? "Placing Order…" : `Place Order Securely · ₹${fmt(total)}`}
              </button>
            </div>
          </div>

          {/* ─── RIGHT: Order Summary ─────────────────────────────────── */}
          <div className="lg:w-96 flex-shrink-0 lg:sticky lg:top-6 self-start space-y-4">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <h3 className="text-sm font-black text-slate-900">
                  Order Summary <span className="text-slate-400 font-medium">({totalItems} {totalItems === 1 ? "item" : "items"})</span>
                </h3>
                <Link to="/cart" className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1">
                  Edit Cart <ChevronRight className="w-3 h-3" />
                </Link>
              </div>

              {/* Items */}
              <div className="px-5 pt-2 pb-0 max-h-72 overflow-y-auto">
                {cart.map(item => (
                  <OrderItemRow
                    key={item.id}
                    item={item}
                    onQtyChange={handleQtyChange}
                    onRemove={handleRemove}
                  />
                ))}
              </div>

              {/* Coupon */}
              <div className="px-5 pt-4 pb-0">
                <button
                  onClick={() => setCouponOpen(v => !v)}
                  className="w-full flex items-center justify-between py-2.5 text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors"
                  data-testid="checkout-coupon-toggle"
                >
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    {couponApplied
                      ? <span className="text-green-600">Coupon "{coupon}" applied — saving ₹{fmt(couponDiscount)}</span>
                      : "Apply Coupon Code"}
                  </div>
                  <ChevronDown className={`w-4 h-4 transition-transform text-slate-400 ${couponOpen ? "rotate-180" : ""}`} />
                </button>
                {couponOpen && (
                  <div className="pb-4">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={coupon}
                        onChange={e => { setCoupon(e.target.value.toUpperCase()); setCouponError(""); setCouponMsg(""); }}
                        placeholder="Enter coupon code"
                        disabled={couponApplied}
                        data-testid="checkout-coupon-input"
                        className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
                      />
                      {couponApplied ? (
                        <button
                          onClick={() => { setCouponApplied(false); setCouponDiscount(0); setCoupon(""); setCouponMsg(""); setCouponError(""); }}
                          className="px-4 bg-red-50 hover:bg-red-100 text-red-500 text-sm font-bold rounded-lg border border-red-200 transition-colors"
                        >
                          Remove
                        </button>
                      ) : (
                        <button
                          onClick={async () => {
                            if (!coupon.trim()) { setCouponError("Enter a coupon code"); return; }
                            setCouponApplying(true); setCouponError(""); setCouponMsg("");
                            try {
                              const res = await axios.post(`${API}/coupons/validate`, { code: coupon, cart_total: subtotal });
                              setCouponDiscount(res.data.discount);
                              setCouponApplied(true);
                              setCouponMsg(res.data.message);
                            } catch (e) {
                              setCouponError(e.response?.data?.detail || "Invalid coupon code");
                            } finally { setCouponApplying(false); }
                          }}
                          disabled={couponApplying}
                          data-testid="checkout-coupon-apply"
                          className="px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-lg transition-colors disabled:opacity-50"
                        >
                          {couponApplying ? "…" : "Apply"}
                        </button>
                      )}
                    </div>
                    {couponError && <p className="text-xs text-red-500 mt-1.5">{couponError}</p>}
                    {couponMsg && <p className="text-xs text-green-600 mt-1.5 font-medium">{couponMsg}</p>}
                  </div>
                )}
              </div>

              <div className="border-t border-slate-100 mx-5" />

              {/* GST Invoice */}
              <div className="px-5 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <button
                      data-testid="gst-checkbox"
                      onClick={() => setGstOpen(true)}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        selectedGst
                          ? "bg-blue-600 border-blue-600"
                          : "border-slate-300 hover:border-blue-400"
                      }`}
                    >
                      {selectedGst && (
                        <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                          <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                    <span className="text-sm font-semibold text-slate-800">GST Invoice</span>
                  </div>
                  <button
                    data-testid="gst-change-btn"
                    onClick={() => setGstOpen(true)}
                    className="text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    {selectedGst ? "Change" : "Add"}
                  </button>
                </div>

                {selectedGst && (
                  <div className="mt-2.5 pl-[30px]">
                    <p className="text-sm font-bold text-slate-800 font-mono tracking-wider">{selectedGst.gstin}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{selectedGst.business_name}</p>
                    <span className="inline-block mt-1.5 text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">B2B Invoice</span>
                  </div>
                )}

                {!selectedGst && (
                  <p className="text-xs text-slate-400 mt-1.5 pl-[30px]">
                    Add GSTIN to receive a B2B tax invoice for this order
                  </p>
                )}
              </div>

              <div className="border-t border-slate-100 mx-5" />
              <div className="px-5 py-4 space-y-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal ({totalItems} {totalItems === 1 ? "item" : "items"})</span>
                  <span className="font-semibold text-slate-800">₹{fmt(subtotal)}</span>
                </div>
                {itemDiscount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Discount</span>
                    <span className="font-semibold text-green-600">−₹{fmt(itemDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 flex items-center gap-1">
                    Shipping Charges
                    {selectedDeliveryOption && (
                      <span className="text-[10px] text-slate-400 border border-slate-200 rounded px-1">
                        {selectedDeliveryOption.name}
                      </span>
                    )}
                  </span>
                  <span className={`font-semibold ${shippingCharge === 0 ? "text-green-600" : "text-slate-800"}`}>
                    {shippingCharge === 0 ? "FREE" : `₹${fmt(shippingCharge)}`}
                  </span>
                </div>
                {paymentSurcharge > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Payment Charges</span>
                    <span className="font-semibold text-amber-700">+₹{fmt(paymentSurcharge)}</span>
                  </div>
                )}
                {couponDiscount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600 flex items-center gap-1">
                      <Tag className="w-3 h-3" /> Coupon ({coupon})
                    </span>
                    <span className="font-semibold text-green-600">−₹{fmt(couponDiscount)}</span>
                  </div>
                )}
              </div>

              {displayShipments.length > 1 && (
                <div className="px-5 py-3 border-t border-slate-100 space-y-2" data-testid="checkout-shipments">
                  <p className="text-xs font-bold text-slate-700">Your order ships in {displayShipments.length} shipments</p>
                  {displayShipments.map(sh => (
                    <div key={sh.id} className="text-xs bg-slate-50 rounded-lg p-2.5">
                      <div className="flex justify-between font-semibold text-slate-800">
                        <span>{sh.name} · {sh.dispatchText}</span>
                        <span>{selectedDeliveryOption?.pricing === "weight" ? (sh.charge === 0 ? "FREE" : `₹${fmt(sh.charge)}`) : ""}</span>
                      </div>
                      <p className="text-slate-500 mt-0.5">{sh.rows.map(r => `${[r.line.color, r.line.size].filter(Boolean).join(" / ") || r.item.name} × ${r.line.qty}`).join(", ")}</p>
                      <p className="text-slate-400">Weight: {sh.weightG >= 1000 ? `${(sh.weightG / 1000).toFixed(2)}kg` : `${sh.weightG}g`}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t-2 border-dashed border-slate-200 mx-5" />

              {/* Total */}
              <div className="px-5 py-4">
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-base font-black text-slate-900">Total Amount</p>
                    <p className="text-[11px] text-slate-400">Inclusive of all applicable charges</p>
                  </div>
                  <p className="text-xl font-black text-slate-900" data-testid="checkout-total">₹{fmt(total)}</p>
                </div>
              </div>

              {/* CTA */}
              <div className="px-5 pb-5">
                <button
                  onClick={handlePlaceOrder}
                  disabled={placing}
                  data-testid="checkout-place-order-btn"
                  className="w-full flex items-center justify-center gap-2.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-colors text-sm shadow-md hover:shadow-lg"
                >
                  <Lock className="w-4 h-4 flex-shrink-0" />
                  {placing ? "Placing Order…" : "Place Order Securely"}
                </button>

                {/* Security note */}
                <div className="flex items-center justify-center gap-1.5 mt-3 text-slate-400">
                  <Shield className="w-3.5 h-3.5" />
                  <span className="text-[11px]">100% Secure Payments</span>
                </div>
                <p className="text-[11px] text-center text-slate-400 mt-0.5">
                  Your payment information is safe with us.
                </p>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="bg-white rounded-xl border border-slate-100 px-5 py-4 shadow-sm">
              <div className="grid grid-cols-3 gap-4">
                {[
                  freeThreshold > 0
                    ? { Icon: Truck, label: "Free Shipping", sub: `On orders above ₹${fmt(freeThreshold)}` }
                    : { Icon: Truck, label: "Doorstep Delivery", sub: "Charged by order weight" },
                  { Icon: RotateCcw, label: "Easy Returns", sub: "7 days return policy" },
                  { Icon: Shield, label: "Secure Payments", sub: "100% safe & secure" },
                ].map(({ Icon, label, sub }) => (
                  <div key={label} className="flex flex-col items-center text-center gap-1.5">
                    <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center">
                      <Icon className="w-4 h-4 text-blue-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-700 leading-tight">{label}</p>
                    <p className="text-[10px] text-slate-400 leading-tight">{sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>{/* end desktop wrapper */}

    {/* GST Drawer */}
    <GstDrawer
      open={gstOpen}
      onClose={() => setGstOpen(false)}
      gstList={gstList}
      selectedId={selectedGstId}
      onSave={handleGstSave}
      onDelete={handleGstDelete}
      onConfirm={handleGstConfirm}
    />
    <SiteFooter />
    </>
  );
}

// ── Checkout Header ───────────────────────────────────────────────────────────
function CheckoutHeader({ cartCount, freeThreshold }) {
  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
      {/* Top bar */}
      <div className="bg-slate-900 text-slate-300 text-xs py-1.5 hidden sm:block">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5"><Phone className="w-3 h-3" />Toll-free: (091) 123-4567</span>
            <span className="flex items-center gap-1.5"><Mail className="w-3 h-3" />support@importrend.com</span>
          </div>
          <span className="font-medium text-slate-200">{freeThreshold > 0 ? `Free Shipping Over ₹${fmt(freeThreshold)}*` : ""}</span>
          <div className="flex items-center gap-4">
            <span>Track Order</span>
            <span>Need Help?</span>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="max-w-7xl mx-auto px-4 py-3.5">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex-shrink-0">
            <BrandLogo size="md" />
          </Link>

          <div className="flex-1 max-w-2xl hidden sm:flex">
            <div className="flex w-full border border-slate-300 rounded-lg overflow-hidden">
              <input
                placeholder="Search for products, brands and more..."
                className="flex-1 px-4 py-2.5 text-sm focus:outline-none text-slate-700 placeholder-slate-400"
              />
              <button className="bg-orange-500 hover:bg-orange-600 transition-colors px-4 flex items-center">
                <Search className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4 ml-auto sm:ml-0">
            <button className="hidden sm:flex flex-col items-center text-slate-600 hover:text-orange-500 transition-colors text-xs gap-0.5">
              <Heart className="w-5 h-5" />
              <span>Wish Lists</span>
            </button>
            <Link to="/customer-login" className="hidden sm:flex flex-col items-center text-slate-600 hover:text-orange-500 transition-colors text-xs gap-0.5">
              <User className="w-5 h-5" />
              <span>Sign in</span>
            </Link>
            <Link to="/cart" className="flex flex-col items-center text-slate-600 hover:text-orange-500 transition-colors text-xs gap-0.5 relative">
              <ShoppingCart className="w-5 h-5" />
              <span>Cart</span>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {cartCount > 9 ? "9+" : cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
